import { Maximize, Minimize } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { Button, CloseButton, Heading, Pressable } from '../../../shared/ui';
import { IMacCardInfo } from '../../../store/mac.slice';

import { MacCardsSelected } from './mac-card-selected';

export interface MacGroupPsychologistResultsProps {
  /** Список identity участников на встрече (кроме психолога) — ждём ответа от каждого */
  expectedParticipantIds: string[];
  /** Имена для отображения в табах (identity -> displayName) */
  participantDisplayNames?: Record<string, string>;
  onClose: () => void;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба: номер участника и его карты — для трансляции клиентам */
  onActiveTabChange?: (participantLabel: string, cards: IMacCardInfo[]) => void;
}

const GROUP_MAC_INSTRUCTIONS = [
  {
    title: 'Если карты рубашкой вверх:',
    lines: [
      'Колода закрыта.',
      'При выборе карта подсвечивается жёлтым, но остаётся закрытой.',
      'Можно выбрать до 4 карт.',
      'Когда клиент закончит выбор, он нажимает «Выбрать» — карты открываются у вас и клиента. Остальные скрываются.',
    ],
  },
  { title: 'Завершение работы:', lines: ['Нажмите Х (крестик).'] },
  { title: 'Новый расклад:', lines: ['Повторно нажмите иконку МАК-карт в нижней части экрана.'] },
];

const GROUP_MAC_IMPORTANT_TEXT =
  'ВАЖНО! Если карты выбираются в режиме рубашкой вверх, то они не должны переворачиваться, клиент увидит их в открытом виде после выбора. А если они показываются в режиме рубашкой вниз, то клиент видит изображение и подсвечиваются выбранные карты.';

/**
 * Нижняя панель психолога в групповой терапии после «Показать колоду»:
 * Сверху — счётчик ответивших, инструкции и «Клиенты думают…» пока не все ответили; затем табы с результатами.
 */
export const MacGroupPsychologistResults: FC<MacGroupPsychologistResultsProps> = ({
  expectedParticipantIds,
  participantDisplayNames,
  onClose,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const clientSelectionsByParticipant = useAppSelector((state) => state.macStateSlice.clientSelectionsByParticipant);

  const respondedIds = expectedParticipantIds.filter((id) => clientSelectionsByParticipant[id]?.length > 0);
  const allResponded = expectedParticipantIds.length > 0 && respondedIds.length >= expectedParticipantIds.length;
  const [forceShowResults, setForceShowResults] = useState(false);
  const canShowResults = allResponded || forceShowResults;

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const effectiveActiveId = activeTabId && respondedIds.includes(activeTabId) ? activeTabId : (respondedIds[0] ?? null);

  const participantNumber = (index: number) => index + 1;

  const initialTabSyncDone = useRef(false);
  useEffect(() => {
    if (!canShowResults || respondedIds.length === 0 || !onActiveTabChange) return;
    if (initialTabSyncDone.current) return;
    initialTabSyncDone.current = true;
    const firstId = respondedIds[0];
    setActiveTabId(firstId);
    const idx = expectedParticipantIds.indexOf(firstId);
    const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
    const labelForClient = participantDisplayNames?.[firstId] ?? numLabel;
    const cards = clientSelectionsByParticipant[firstId] ?? [];
    onActiveTabChange(labelForClient, cards);
  }, [
    canShowResults,
    respondedIds,
    expectedParticipantIds,
    clientSelectionsByParticipant,
    participantDisplayNames,
    onActiveTabChange,
  ]);

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-white rounded-chip md:rounded-card overflow-hidden">
      <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
        {onExpandToggle && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="pointer-events-auto"
            aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
            startIcon={isExpanded ? <Minimize size={16} aria-hidden /> : <Maximize size={16} aria-hidden />}
            onClick={onExpandToggle}
          >
            {isExpanded ? 'Свернуть' : 'Расширить'}
          </Button>
        )}
        <CloseButton className="pointer-events-auto" onClick={onClose} />
      </div>
      <div
        className={classNames(
          'relative flex items-center gap-3 flex-shrink-0 p-4 pb-2',
          !canShowResults && 'border-b border-line',
        )}
      >
        <div className="flex items-baseline gap-3 min-w-0 flex-1 flex-wrap">
          <Heading size="lg" className="shrink-0">
            МАК-карты
          </Heading>
          {canShowResults && (
            <p className="text-ink-muted text-body-xl truncate min-w-0">
              Клиенты сделали свой выбор. Выберите участника, чтобы посмотреть результат.
            </p>
          )}
        </div>
        {!canShowResults && expectedParticipantIds.length > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <p className="text-ink text-body-xl-strong tabular-nums">
              Ответили: {respondedIds.length} из {expectedParticipantIds.length}
            </p>
            {respondedIds.length > 0 && (
              <Button size="sm" variant="primary" onClick={() => setForceShowResults(true)}>
                Показать результаты
              </Button>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0" aria-hidden />
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {!canShowResults ? (
          <>
            {/* Инструкции и сообщение «Клиенты думают…» — при переполнении прокрутка */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 white-scroll">
              <div className="flex flex-col gap-5 text-ink text-body-xl">
                {GROUP_MAC_INSTRUCTIONS.map((block, i) => (
                  <div key={i}>
                    <p className="mb-1.5 text-heading-md">{block.title}</p>
                    <ul className="list-disc list-inside space-y-1 text-ink-muted text-body-xl">
                      {block.lines.map((line, j) => (
                        <li key={j}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="text-ink text-heading-md">{GROUP_MAC_IMPORTANT_TEXT}</p>
              </div>
            </div>
            <div className="flex-shrink-0 px-4 pb-4 flex flex-col items-center gap-2">
              <span className="inline-flex items-center px-10 py-2 rounded-control bg-primary/50 text-white text-body-xl-strong">
                Клиенты думают...
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 pb-3 mb-3 shrink-0 px-4">
              {respondedIds.map((id) => {
                const idx = expectedParticipantIds.indexOf(id);
                return (
                  <Pressable
                    key={id}
                    onClick={() => {
                      setActiveTabId(id);
                      const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
                      const labelForClient = participantDisplayNames?.[id] ?? numLabel;
                      const cards = clientSelectionsByParticipant[id] ?? [];
                      onActiveTabChange?.(labelForClient, cards);
                    }}
                    title={participantDisplayNames?.[id]}
                    aria-pressed={effectiveActiveId === id}
                    className={classNames(
                      'flex items-center justify-center w-9 h-9 rounded-full text-body-xl-strong transition-colors',
                      effectiveActiveId === id
                        ? 'bg-primary text-white border-2 border-white'
                        : 'bg-line text-ink hover:bg-line-strong',
                    )}
                  >
                    {participantNumber(idx >= 0 ? idx : 0)}
                  </Pressable>
                );
              })}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-4 pb-4">
              {effectiveActiveId && clientSelectionsByParticipant[effectiveActiveId]?.length > 0 ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  <MacCardsSelected
                    selectedCards={clientSelectionsByParticipant[effectiveActiveId]}
                    fillContainerHeight
                  />
                </div>
              ) : (
                <p className="text-ink-muted text-body-xl">
                  {respondedIds.includes(effectiveActiveId ?? '') ? 'Нет выбранных карт' : 'Участник ещё не ответил'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
